/**
 * Story 首免/自动拍策略只读与领取（C 域）SDK。
 *
 * 只包装 C 已稳定的 HTTP 合同：本人资格/授权状态、一次性 claim、关闭回执。
 * 不触发收费供应商、不写媒体/任务终态；媒体一律走 storyFilmLibrary。
 */

const PREFIX = '/api/visual-game/story-film/policy'

/**
 * @param {{ request: Function }} transport
 * @param {{ prefix?: string }} [options]
 */
export function createStoryFilmPolicyApi(transport, { prefix = PREFIX } = {}) {
  if (!transport || typeof transport.request !== 'function') {
    throw new Error('createStoryFilmPolicyApi: transport.request is required')
  }
  return {
    /**
     * 本人首免资格与（可选）当前 run/branch 的自动拍授权状态。
     * @param {{ run_id?: string|null, branch_id?: string|null }} [params]
     */
    status({ run_id = null, branch_id = null } = {}) {
      const params = new URLSearchParams()
      if (run_id !== null && run_id !== '') params.set('run_id', String(run_id))
      if (branch_id !== null && branch_id !== '') params.set('branch_id', String(branch_id))
      const query = params.toString()
      return transport.request('GET', `${prefix}/status${query ? `?${query}` : ''}`, { parse: parsePolicyStatus })
    },
    /**
     * 一次性领取首免弹窗。200 的 can_show=false 也是正常结果（不抛错）。
     * @param {{ episode_id: string, media_version: string, run_id: string, branch_id: string }} body
     */
    claimFirstFree(body) {
      return transport.request('POST', `${prefix}/first-free/claim`, {
        body: {
          episode_id: requireString(body?.episode_id, 'episode_id'),
          media_version: requireString(body?.media_version, 'media_version'),
          run_id: requireString(body?.run_id, 'run_id'),
          branch_id: requireString(body?.branch_id, 'branch_id'),
        },
        parse: parseClaimResult,
      })
    },
    /** 关闭回执：同 token 幂等，不生成第二条领取。 */
    closeFirstFree({ claim_token } = {}) {
      return transport.request('POST', `${prefix}/first-free/close`, {
        body: { claim_token: requireString(claim_token, 'claim_token') },
        parse: parseCloseResult,
      })
    },
    /**
     * 自动拍一次同意的报价预览（真实合同，只读）。
     * 返回服务端测算的本集参考价与 quote_id；参考价不等于玩家上限。
     * @param {{ run_id: string, branch_id: string, episode_id: string }} params
     */
    consentPreview({ run_id, branch_id, episode_id } = {}) {
      const params = new URLSearchParams({
        run_id: requireString(run_id, 'run_id'),
        branch_id: requireString(branch_id, 'branch_id'),
        episode_id: requireString(episode_id, 'episode_id'),
      })
      return transport.request('GET', `${prefix}/auto/consent-preview?${params.toString()}`, { parse: parseConsentPreview })
    },
    /**
     * 一次明确同意：只传定位三个参数 + 玩家上限 + 预览返回的 quote_id；内部模型/汇率/起点由服务端定。
     * 政策未开放时服务端返回 draft（不生效、不扣费），本方法不触发供应商。
     * @param {{ run_id: string, branch_id: string, episode_id: string, price_ceiling_points: number, quote_id: string }} body
     */
    consent(body = {}) {
      return transport.request('POST', `${prefix}/auto/consent`, {
        body: {
          run_id: requireString(body.run_id, 'run_id'),
          branch_id: requireString(body.branch_id, 'branch_id'),
          episode_id: requireString(body.episode_id, 'episode_id'),
          price_ceiling_points: requirePositiveInteger(body.price_ceiling_points, 'price_ceiling_points'),
          quote_id: requireString(body.quote_id, 'quote_id'),
          confirmed: true,
        },
        parse: parsePolicyStatus,
      })
    },
    /**
     * 显式确认自动拍：episode/run/branch + 报价上限 + 成本模型/系数 + 生效点缺一不可。
     * 政策未批准时服务端只写 draft 并返回 approved=false/detail=policy_unapproved；
     * 本方法不触发首免、不调用供应商、不扣费。
     * @param {{ episode_id: string, run_id: string, branch_id: string, price_ceiling_points: number, cost_model_version: string, coefficient_ppm: number, effective_from_turn: number }} body
     */
    authorizeAuto(body = {}) {
      return transport.request('POST', `${prefix}/auto/authorize`, {
        body: {
          episode_id: requireString(body.episode_id, 'episode_id'),
          run_id: requireString(body.run_id, 'run_id'),
          branch_id: requireString(body.branch_id, 'branch_id'),
          price_ceiling_points: requirePositiveInteger(body.price_ceiling_points, 'price_ceiling_points'),
          cost_model_version: requireString(body.cost_model_version, 'cost_model_version'),
          coefficient_ppm: requirePositiveInteger(body.coefficient_ppm, 'coefficient_ppm'),
          effective_from_turn: requireNonNegativeInteger(body.effective_from_turn, 'effective_from_turn'),
        },
        parse: parseAuthorizeResult,
      })
    },
    /** 暂停只挡新受理，不取消已在制集；已被暂停/无授权返回 AUTO_NOT_AUTHORIZED。 */
    pauseAuto({ run_id, branch_id, reason = 'paused_user' } = {}) {
      return transport.request('POST', `${prefix}/auto/pause`, {
        body: {
          run_id: requireString(run_id, 'run_id'),
          branch_id: requireString(branch_id, 'branch_id'),
          reason: requirePauseReason(reason),
        },
        parse: parsePolicyStatus,
      })
    },
  }
}

export function parsePolicyStatus(value) {
  if (!isObject(value) || !isObject(value.first_free) || !isObject(value.capabilities)) {
    throw new Error('Story film policy status response is invalid')
  }
  return {
    first_free: {
      status: requireString(value.first_free.status, 'first_free.status'),
      episode_id: optionalString(value.first_free.episode_id),
      submission_id: value.first_free.submission_id === null || value.first_free.submission_id === undefined ? null : requireNumber(value.first_free.submission_id),
      reserved_at: optionalString(value.first_free.reserved_at),
      consumed_at: optionalString(value.first_free.consumed_at),
      // C 的只读门禁字段：旧响应缺字段时解析为 null，调用方必须 fail closed（不得当成获准）。
      entry: optionalString(value.first_free.entry),
      lock_status: optionalString(value.first_free.lock_status),
      locked_here: optionalBoolean(value.first_free.locked_here),
      locked_elsewhere: optionalBoolean(value.first_free.locked_elsewhere),
      hint_allowed: optionalBoolean(value.first_free.hint_allowed),
      candidate_ordering_verified: optionalBoolean(value.first_free.candidate_ordering_verified),
      reason: optionalString(value.first_free.reason),
      media_ready: optionalBoolean(value.first_free.media_ready),
    },
    capabilities: {
      first_free_claim: requireString(value.capabilities.first_free_claim, 'capabilities.first_free_claim'),
      auto_event_engage: optionalString(value.capabilities.auto_event_engage),
      auto_execution: optionalString(value.capabilities.auto_execution),
      current_cost_model_version: optionalString(value.capabilities.current_cost_model_version),
      historical_backfill: value.capabilities.historical_backfill === true,
      retry: value.capabilities.retry === true,
      retake: optionalString(value.capabilities.retake),
    },
    auto: value.auto === null || value.auto === undefined ? null : value.auto,
    // 服务端钟的入场时刻（S01A 在场判定用）：旧响应没有该字段时为 null，调用方必须 fail closed，
    // 不得用设备 Date.now() 或更晚的详情时间冒充入场。
    server_now: optionalString(value.server_now),
  }
}

function parseAuthorizeResult(value) {
  const parsed = parsePolicyStatus(value)
  if (typeof value.approved !== 'boolean') throw new Error('Story film auto authorize response is invalid: approved')
  return { ...parsed, approved: value.approved, detail: optionalString(value.detail) }
}

function parseClaimResult(value) {
  if (!isObject(value) || typeof value.can_show !== 'boolean') {
    throw new Error('Story film first-free claim response is invalid')
  }
  return {
    can_show: value.can_show,
    already_claimed: value.already_claimed === true,
    reason: optionalString(value.reason),
    claim_token: optionalString(value.claim_token),
  }
}

function parseCloseResult(value) {
  if (!isObject(value) || typeof value.closed !== 'boolean') {
    throw new Error('Story film first-free close response is invalid')
  }
  return { closed: value.closed }
}

/**
 * 解析自动拍报价预览：口径或字段不符直接抛错，绝不用本地默认值冒充真实报价。
 * @param {unknown} value
 * @returns {{ reference_price_points: number, quote_id: string, future_price_rule: string, starts: string }}
 */
export function parseConsentPreview(value) {
  if (!isObject(value)) throw new Error('Story film auto consent preview response is invalid')
  if (value.future_price_rule !== 'actual_quote_up_to_user_limit') {
    throw new Error(`Story film auto consent preview rule unsupported: ${String(value.future_price_rule)}`)
  }
  if (value.starts !== 'after_confirmation') {
    throw new Error(`Story film auto consent preview start unsupported: ${String(value.starts)}`)
  }
  return {
    reference_price_points: requirePositiveInteger(value.reference_price_points, 'reference_price_points'),
    quote_id: requireString(value.quote_id, 'quote_id'),
    future_price_rule: value.future_price_rule,
    starts: value.starts,
  }
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function requireString(value, field) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Story film policy field is invalid: ${field}`)
  }
  return value
}

function optionalString(value) {
  return value === null || value === undefined ? null : String(value)
}

/** 只读门禁布尔：非布尔（含缺失/字符串/数字）一律 null，由调用方 fail closed。 */
function optionalBoolean(value) {
  return value === true || value === false ? value : null
}

function requireNumber(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) throw new Error('Story film policy field is invalid: submission_id')
  return number
}

function requirePositiveInteger(value, field) {
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 1) throw new Error(`Story film policy field is invalid: ${field}`)
  return number
}

function requireNonNegativeInteger(value, field) {
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 0) throw new Error(`Story film policy field is invalid: ${field}`)
  return number
}

function requirePauseReason(value) {
  if (value !== 'paused_user' && value !== 'paused_funds') throw new Error('Story film policy field is invalid: reason')
  return value
}
